import { useCallback, useEffect, useState } from "react";
import type { Incident } from "@feedback-hub/contract/browser";

import { Detail } from "./components/Detail.js";
import { Mail } from "./components/Mail.js";
import { Metrics } from "./components/Metrics.js";
import { Queue } from "./components/Queue.js";
import { mailTabFromPath, mailTabPath, mailTabTitle, type MailTab } from "./mailTabs.js";
import { formatPageTitle, useDocumentTitle } from "./pageTitle.js";
import { AuthProvider, RequireAuth, useAuth } from "./useAuth.js";

type Route =
  | { name: "queue" }
  | { name: "metrics" }
  | { name: "mail"; tab: MailTab }
  | { name: "detail"; appId: string; incidentId: string };

// Tab title section per route. A Record over the union, so a new route without a title is a
// compile error. Detail sets its own title (ticket ref + this section) once the incident loads.
const ROUTE_TITLES: Record<Route["name"], string> = {
  queue: "Queue",
  metrics: "Metrics",
  mail: "Mail",
  detail: "Queue",
};

function parseRoute(pathname: string): Route {
  if (pathname === "/metrics") return { name: "metrics" };
  const mailTab = mailTabFromPath(pathname);
  if (mailTab) return { name: "mail", tab: mailTab };
  const detail = /^\/incidents\/([^/]+)\/([^/]+)\/?$/.exec(pathname);
  if (detail) {
    return {
      name: "detail",
      appId: decodeURIComponent(detail[1]!),
      incidentId: decodeURIComponent(detail[2]!),
    };
  }
  return { name: "queue" };
}

function navigate(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function Shell({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const route = useRoute();
  const email = auth.user?.email ?? "admin";

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand">
          <h1>FeedbackHub</h1>
          <div className="sub">Incident queue · {email}</div>
        </div>
        <nav className="nav">
          <button
            type="button"
            className="linkish"
            aria-current={route.name === "queue" || route.name === "detail" ? "page" : undefined}
            onClick={() => navigate("/")}
          >
            Queue
          </button>
          <button
            type="button"
            className="linkish"
            aria-current={route.name === "metrics" ? "page" : undefined}
            onClick={() => navigate("/metrics")}
          >
            Metrics
          </button>
          <button
            type="button"
            className="linkish"
            aria-current={route.name === "mail" ? "page" : undefined}
            onClick={() => navigate("/mail")}
          >
            Mail
          </button>
          <button type="button" className="linkish" onClick={() => auth.signOut()}>
            Sign out
          </button>
        </nav>
      </header>
      {children}
    </div>
  );
}

function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return route;
}

function AuthenticatedApp() {
  const route = useRoute();
  useDocumentTitle(
    route.name === "detail"
      ? null
      : route.name === "mail"
        ? mailTabTitle(route.tab)
        : formatPageTitle(ROUTE_TITLES[route.name]),
  );

  const openIncident = useCallback((incident: Incident) => {
    navigate(
      `/incidents/${encodeURIComponent(incident.appId)}/${encodeURIComponent(incident.incidentId)}`,
    );
  }, []);

  if (route.name === "metrics") {
    return (
      <Shell>
        <Metrics />
      </Shell>
    );
  }

  if (route.name === "mail") {
    return (
      <Shell>
        <Mail tab={route.tab} onSelectTab={(t) => navigate(mailTabPath(t))} />
      </Shell>
    );
  }

  if (route.name === "detail") {
    return (
      <Shell>
        <Detail
          appId={route.appId}
          incidentId={route.incidentId}
          onBack={() => navigate("/")}
          onUpdated={() => {
            /* stay on detail; Detail already refreshed local state */
          }}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <Queue onOpen={openIncident} />
    </Shell>
  );
}

export function App() {
  return (
    <AuthProvider>
      <RequireAuth>
        <AuthenticatedApp />
      </RequireAuth>
    </AuthProvider>
  );
}
